// Singla cart: shared logic for the slide-out cart (<cart-notification>) and the cart page
// (<singla-cart-page>). Both render snippets/singla-cart-drawer-content.liquid. On each element,
// data-section names the section to re-render and data-content the element to swap.
if (!customElements.get('singla-cart-page')) {
  class SinglaCart extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.startTimer();
    }

    close() {}

    getSectionsToRender() {
      return [
        { id: this.dataset.section, selector: `#${this.dataset.content}` },
        { id: 'cart-icon-bubble' }
      ];
    }

    get content() {
      return this.querySelector(`#${this.dataset.content}`);
    }

    applySections(sections) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        if (section.id === this.dataset.section) {
          const fresh = doc.querySelector(section.selector);
          if (fresh && this.content) this.content.replaceWith(fresh);
        } else {
          const target = document.getElementById(section.id);
          const fresh = doc.querySelector('.shopify-section');
          if (target && fresh) target.innerHTML = fresh.innerHTML;
        }
      });
      this.tick();
    }

    request(url, body) {
      this.busy = true;
      this.classList.add('is-loading');
      body.sections = this.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      })
        .then((res) => res.json())
        .then((res) => {
          if (res.status && res.description) throw new Error(res.description);
          if (res.sections) this.applySections(res.sections);
          return res;
        })
        .catch((err) => {
          console.error(err);
          this.refresh();
        })
        .finally(() => {
          this.busy = false;
          this.classList.remove('is-loading');
        });
    }

    refresh() {
      const ids = this.getSectionsToRender().map((s) => s.id).join(',');
      return fetch(`${window.location.pathname}?sections=${ids}`)
        .then((res) => res.json())
        .then((sections) => {
          this.applySections(sections);
          this.syncGifts();
        });
    }

    // Free gifts: add or remove them to match the spend thresholds.
    syncGifts() {
      const c = this.content;
      if (!c || this.syncing) return;
      const subtotal = Number(c.dataset.subtotal || 0);
      const updates = {};
      if (subtotal >= Number(c.dataset.gift1Threshold)) {
        if (!c.dataset.gift1Key && c.dataset.gift1Variant) updates[c.dataset.gift1Variant] = 1;
        else if (Number(c.dataset.gift1Qty) > 1) updates[c.dataset.gift1Key] = 1;
      } else if (c.dataset.gift1Key) {
        updates[c.dataset.gift1Key] = 0;
      }
      if (c.dataset.gift2Key) {
        if (subtotal < Number(c.dataset.gift2Threshold)) updates[c.dataset.gift2Key] = 0;
        else if (Number(c.dataset.gift2Qty) > 1) updates[c.dataset.gift2Key] = 1;
      }
      if (!Object.keys(updates).length) return;
      this.syncing = true;
      this.request('/cart/update.js', { updates }).finally(() => {
        this.syncing = false;
      });
    }

    afterChange(promise) {
      return promise.then(() => this.syncGifts());
    }

    onClick(evt) {
      if (evt.target.closest('[data-scd-close]')) {
        this.close();
        return;
      }
      if (this.busy) return;

      const qtyBtn = evt.target.closest('[data-scd-qty]');
      if (qtyBtn) {
        const item = qtyBtn.closest('.scd__item');
        this.afterChange(this.request('/cart/change.js', { id: item.dataset.key, quantity: Number(qtyBtn.dataset.scdQty) }));
        return;
      }

      const giftBtn = evt.target.closest('[data-scd-add]');
      if (giftBtn) {
        this.afterChange(this.request('/cart/add.js', { items: [{ id: Number(giftBtn.dataset.scdAdd), quantity: 1 }] }));
        return;
      }

      const upsellBtn = evt.target.closest('[data-scd-upsell]');
      if (upsellBtn) {
        const select = this.querySelector('[data-scd-upsell-variant]');
        const id = Number(select ? select.value : upsellBtn.dataset.scdUpsell);
        upsellBtn.textContent = 'Adding…';
        this.afterChange(this.request('/cart/add.js', { items: [{ id, quantity: 1 }] }));
      }
    }

    onChange(evt) {
      const item = evt.target.closest('.scd__item');
      if (!item || this.busy) return;
      const quantity = Number(item.dataset.quantity);
      let newId = null;

      if (evt.target.matches('[data-scd-colour]')) {
        // Colours are separate products: keep the same size in the new colour.
        const option = evt.target.selectedOptions[0];
        const variants = JSON.parse(option.dataset.variants || '[]');
        const sizeSelect = item.querySelector('[data-scd-option="0"]');
        const size = sizeSelect ? sizeSelect.value : null;
        const match = variants.find((v) => v.title === size);
        if (!match || !match.available) {
          const current = evt.target.querySelector('[selected]');
          if (current) evt.target.value = current.value;
          this.flash(item, `${size || 'That size'} is sold out in ${option.textContent.trim()}`);
          return;
        }
        newId = match.id;
      } else if (evt.target.matches('[data-scd-option]')) {
        const variants = JSON.parse(item.querySelector('[data-scd-variants]').textContent);
        const picked = Array.from(item.querySelectorAll('[data-scd-option]')).map((s) => s.value);
        const match = variants.find((v) => v.options.every((o, i) => o === picked[i]));
        if (!match || !match.available) {
          evt.target.classList.add('is-error');
          this.flash(item, 'That size is sold out');
          return;
        }
        newId = match.id;
      }

      if (!newId || String(newId) === item.dataset.variantId) return;
      // Swap the line: remove the old variant and add the new one in a single update.
      const existing = this.querySelector(`.scd__item[data-variant-id="${newId}"]`);
      const updates = {};
      updates[item.dataset.key] = 0;
      updates[newId] = quantity + (existing ? Number(existing.dataset.quantity) : 0);
      this.afterChange(this.request('/cart/update.js', { updates }));
    }

    flash(item, message) {
      let note = item.querySelector('.scd__item-msg');
      if (!note) {
        note = document.createElement('p');
        note.className = 'scd__item-msg';
        item.querySelector('.scd__item-info').appendChild(note);
      }
      note.textContent = message;
    }

    // "Check out in 09:59" countdown, shared across pages and restarted when it runs out.
    startTimer() {
      const minutes = 10;
      const key = 'singlaCartTimerEnd';
      this.timerEnd = 0;
      try {
        this.timerEnd = Number(localStorage.getItem(key)) || 0;
      } catch (e) {}
      this.resetTimer = () => {
        this.timerEnd = Date.now() + minutes * 60 * 1000;
        try {
          localStorage.setItem(key, String(this.timerEnd));
        } catch (e) {}
      };
      if (this.timerEnd < Date.now()) this.resetTimer();
      this.tick();
      setInterval(() => this.tick(), 1000);
    }

    tick() {
      if (!this.timerEnd) return;
      let left = Math.round((this.timerEnd - Date.now()) / 1000);
      if (left <= 0) {
        this.resetTimer();
        left = Math.round((this.timerEnd - Date.now()) / 1000);
      }
      const text = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
      this.querySelectorAll('[data-scd-timer]').forEach((el) => {
        el.textContent = text;
      });
    }
  }

  // Slide-out cart. Registered as <cart-notification> with the same public methods as Dawn's
  // notification (renderContents, getSectionsToRender, setActiveElement, open, close), so
  // product forms and the bundle builders open it without changes.
  customElements.define('cart-notification', class SinglaCartDrawer extends SinglaCart {
    constructor() {
      super();
      this.notification = this.querySelector('#cart-notification');
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

      // The header cart icon opens the drawer (the cart page has its own full view).
      document.addEventListener('click', (evt) => {
        const icon = evt.target.closest('#cart-icon-bubble');
        if (!icon || this.onCartPage) return;
        evt.preventDefault();
        this.setActiveElement(icon);
        this.open();
      });

      // Keep gifts in line with the cart on every page load (the cart page does its own).
      if (!this.onCartPage) this.syncGifts();
    }

    get onCartPage() {
      return window.location.pathname.startsWith('/cart');
    }

    connectedCallback() {
      // Live outside the header so the drawer sits above the sticky header and announcement bar.
      if (this.parentElement !== document.body) document.body.appendChild(this);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(parsedState) {
      if (parsedState && parsedState.sections && parsedState.sections[this.dataset.section]) {
        this.applySections(parsedState.sections);
        this.syncGifts();
      } else {
        this.refresh();
      }
      this.open();
    }

    open() {
      document.body.classList.add('scd-open');
      this.classList.add('is-open');
      this.notification.focus();
      if (typeof trapFocus === 'function') trapFocus(this.notification);
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('scd-open');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
    }
  });

  // Full cart page (sections/singla-main-cart.liquid).
  customElements.define('singla-cart-page', class SinglaCartPage extends SinglaCart {
    constructor() {
      super();
      this.syncGifts();
    }
  });
}
